import { get, post, put } from "aws-amplify/api";
import { default as React, useState, useEffect, useCallback, useMemo } from "react";
import { GenericTable } from "components/GenericTable";
import EditableCell from "containers/common/EditableCell";
import { PacmanLoader } from "react-spinners";
import Moment from "moment";
import { Alert, Button, Col, Row } from "react-bootstrap";
import { createElementIdFactory, createReusableTableElementIds } from "utils/elementIds";
import "react-datepicker/dist/react-datepicker.css";
import "containers/common/List.css";
import "containers/common/Table.css";
import { PAGE_SIZES } from "utils/pageSizeConfig";

import {
  createColumnHelper,
} from "@tanstack/react-table";

const columnHelper = createColumnHelper();

function SLMRCAKeywordSuppressionList({ isAuthenticated, user }) {
  const isCompanyKeywordsIdPrefix = "is-company-keywords";

  const [isLoading, setIsLoading] = useState(true);
  const [ui, setUi] = useState({ showAlert: false, errorMessage: "" });
  const [sorting, setSorting] = useState([]);
  const [data, setData] = useState([]);

  // --- API Functions ---
  const getData = useCallback(async (filter) => {
    const options = {
      queryParams: filter,
    };

    const response = await get({
      apiName: "colin",
      path: "/slmautosuppressions",
      options,
    }).response;
    try {
      const cs = await response.body.json();
      return cs;
    } catch (error) {
      alert(error + ". Please refresh the page (F5) to try again.");
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    (async () => {
      try {
        const localCityscopes = await getData();
        setData(localCityscopes || []);
        setIsLoading(false);
      } catch (e) {
        alert(e);
      }
      document.title = "Is Company Keywords";
    })();
  }, [isAuthenticated, getData]);

  // --- Save Functions ---
  const saveRecord = useCallback(async (record) => {
    const isNew = record.new_record || !record.owner_is_company_keyword_id;

    const path = isNew
      ? "/slmautosuppressions/new"
      : `/slmautosuppressions/${record.owner_is_company_keyword_id}`;

    const method = isNew ? post : put;

    try {
      const response = await method({
        apiName: "colin",
        path: path,
        options: {
          body: {
            ...record,
            last_edited_by: user
          }
        }
      }).response;

      return await response.body.json();
    } catch (err) {
      console.error("Save error:", err);
      throw err;
    }
  }, [user]);

  // --- Table Logic ---
  const updateTableState = (rowIndex, columnId, value) => {
    setData(old => old.map((record, index) => (
      index === rowIndex
        ? { ...record, [columnId]: value, modified: true }
        : record
    )));
  };

  const handleSave = async ({ row, column, value }) => {
    const newValue = value;
    const recordToSave = { ...row.original, [column.id]: newValue };

    if (recordToSave.new_record && (!recordToSave.search_string || recordToSave.search_string.trim() === "")) {
      setUi({ showAlert: true, errorMessage: "You must enter a word or phrase to suppress." });
      return;
    }

    setData(old => old.map((r, index) =>
      index === row.index ? { ...r, isSaving: true, [column.id]: newValue } : r
    ));

    try {
      const result = await saveRecord(recordToSave);

      if (result.result) {
        setUi({ showAlert: false, errorMessage: "" });

        setData(old => old.map((r, index) =>
          index === row.index
            ? {
              ...r,
              owner_is_company_keyword_id: result.id,
              [column.id]: newValue,
              modified: false,
              isSaving: false,
              new_record: false
            }
            : r
        ));
      } else {
        setUi({ showAlert: true, errorMessage: result.error_message || "Save failed." });
        setData(old => old.map((r, index) => index === row.index ? { ...r, isSaving: false } : r));
      }
    } catch (err) {
      console.error("Save error:", err);
      setData(old => old.map((r, index) => index === row.index ? { ...r, isSaving: false } : r));
      setUi({ showAlert: true, errorMessage: "Network Error: Could not save." });
    }
  };

  const addRecord = () => {
    setData(prev => [{
      search_string: "",
      search_option: "containing",
      notes: "",
      created_by: user,
      last_edited_by: user,
      new_record: true,
    }, ...prev]);
  };

  const handleDeleteRecord = useCallback(async (props, event) => {
    if (event) event.preventDefault();

    const currentIndex = props.row.index;
    const recordToDelete = data[currentIndex];
    const recordId = recordToDelete?.owner_is_company_keyword_id;

    if (!recordId) {
      if (recordToDelete?.new_record) {
        setData(prev => prev.filter((_, i) => i !== currentIndex));
        return;
      }
      setUi({ showAlert: true, errorMessage: "Record ID missing. Please refresh the page." });
      return;
    }

    if (window.confirm(`Are you sure you want to delete "${recordToDelete.search_string}"?`)) {
      try {
        const response = await put({
          apiName: "colin",
          path: `/slmautosuppressions/${recordId}`,
          options: {
            body: {
              ...recordToDelete,
              archived_date: new Date().toISOString(),
              last_edited_by: user
            }
          }
        }).response;

        const result = await response.body.json();

        if (result.result) {
          setData(prev => prev.filter(u => u.owner_is_company_keyword_id !== recordId));
        } else {
          setUi({ showAlert: true, errorMessage: result.error_message });
        }
      } catch (err) {
        console.error("Delete failed", err);
        setUi({ showAlert: true, errorMessage: "Failed to delete record." });
      }
    }
  }, [user, data]);

  const createId = useMemo(
    () => createElementIdFactory(isCompanyKeywordsIdPrefix),
    [isCompanyKeywordsIdPrefix]
  );

  const tableElementIds = useMemo(
    () => createReusableTableElementIds(createId),
    [createId]
  );

  // --- Column Definitions ---
  const columns = useMemo(() => {
    const searchOptions = [
      { value: "containing", label: "Containing" },
      { value: "ending", label: "Ending" },
      { value: "starting", label: "Starting" },
    ];

    return [
      columnHelper.accessor("owner_is_company_keyword_id", {
        id: "owner_is_company_keyword_id",
        header: "ID",
        cell: (info) => info.getValue(),
        size: 60,
      }),
      columnHelper.accessor("search_string", {
        id: "search_string",
        header: "Search String",
        cell: EditableCell,
      }),
      columnHelper.accessor("search_option", {
        id: "search_option",
        header: "Search Option",
        cell: (props) => {
          return <EditableCell {...props} options={searchOptions} dropdownWidth="150px" />;
        },
      }),
      columnHelper.accessor("notes", {
        id: "notes",
        header: "Notes",
        cell: EditableCell,
        size: 120,
      }),
      columnHelper.accessor(
        (d) => {
          if (d.created_date) {
            return Moment(d.created_date).local().format("DD-MM-YYYY");
          } else {
            return null;
          }
        },
        {
          id: "created_date",
          header: "Created Date",
          cell: (info) => info.getValue(),
          size: 60,
        }
      ),
      columnHelper.accessor("owner_is_company_keyword_result_id", {
        id: "owner_is_company_keyword_result_id",
        header: " ",
        cell: (props) => (
          <Button id={createId("delete", "button", props.row.id)} type="button" size="sm" variant="danger" onClick={(e) => handleDeleteRecord(props, e)} >
            Delete
          </Button>
        ),
        size: 60,
        enableSorting: false,
      }),
    ];
  }, [createId, handleDeleteRecord]);

  if (!isAuthenticated) return null;

  // --- Render Functions ---
  return (
    <div className="mt-3">
      <Alert show={ui.showAlert} variant="danger" onClose={() => setUi({ ...ui, showAlert: false })} dismissible>
        {ui.errorMessage}
      </Alert>

      <Row>
        <Col className="text-right" style={{ marginBottom: '12px' }}>
          <Button id={createId("add", "button")} variant="secondary" size="sm" onClick={addRecord}>Add</Button>
        </Col>
      </Row>

      <Row>
        <Col>
          {isLoading ? (
            <div className="outer-div">
              <div className="sweet-loading inner-div">
                <PacmanLoader
                  cssOverride={{ display: "block", margin: "0 auto" }}
                  size={20}
                  color={"#e03c31"}
                  loading={isLoading}
                />
              </div>
            </div>
          ) : (
            <div className="custom-data-table">
              <GenericTable
                columns={columns}
                data={data}
                section="is-company-keywords"
                elementIds={tableElementIds}
                pageSize={PAGE_SIZES.isCompanyKeywords}
                enablePagination={true}
                state={{ sorting }}
                onSortingChange={setSorting}
                enableSortingRemoval={false}
                getRowId={(row) => row.owner_is_company_keyword_id || row.search_string}
                meta={{
                  updateData: updateTableState,
                  handleSave: handleSave,
                }}
              />
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
}

export default SLMRCAKeywordSuppressionList;